import * as React from 'react'

import { action, computed, makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList, toggleClass } from 'react-classlist-helper'

import * as Icons from '~/client/src/shared/components/Icons'
import DeliveryStatus from '~/client/src/shared/constants/DeliveryStatus'
import FieldIds from '~/client/src/shared/enums/DeliveryFieldIds'
import Delivery from '~/client/src/shared/models/Delivery'
import { SharedInfo } from '~/client/src/shared/models/SharedInfo'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import DeliveryVehicleTypesStore from '~/client/src/shared/stores/domain/DeliveryVehicleTypes.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import MaterialCategoryStore from '~/client/src/shared/stores/domain/MaterialCategories.store'
import MaterialsStore from '~/client/src/shared/stores/domain/Materials.store'
import OffloadingEquipmentsStore from '~/client/src/shared/stores/domain/OffloadingEquipments.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import InitialState from '~/client/src/shared/stores/InitialState'
import DeliveryCardFieldsConfigStore, {
  ICardAttribute,
  MAX_NAME_LENGTH,
} from '~/client/src/shared/stores/ui/DeliveryCardFieldsConfig.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { truncateText } from '~/client/src/shared/utils/textUtils'

import { DeliveryIcon } from '../../../DeliveryIcon/DeliveryIcon'
import DeliveryStatusText from '../../../DeliveryStatusText/DeliveryStatusText'
import DeliveryCalendarCardStore, {
  CARD_ROW_HEIGHT,
  MIN_CARD_TITLE_ROW_HEIGHT,
  MIN_CARD_WIDTH,
} from './DeliveryCalendarCard.store'

import Colors from '~/client/src/shared/theme.module.scss'

interface IProps {
  delivery: Delivery
  backgroundColor: string
  color: string
  border: string
  isRotated: boolean
  containerHeight: number
  containerWidth: number
  status: DeliveryStatus

  sharedInfo?: SharedInfo
  isShared?: boolean
  className?: string
  shouldUseMockValues?: boolean

  setIgnoreTooltip?(isIgnored: boolean): void

  state?: InitialState
  projectDateStore?: ProjectDateStore
  locationAttributesStore?: LocationAttributesStore
  deliveryVehicleTypesStore?: DeliveryVehicleTypesStore
  offloadingEquipmentsStore?: OffloadingEquipmentsStore
  companiesStore?: CompaniesStore
  materialsStore?: MaterialsStore
  projectMembersStore?: ProjectMembersStore
  materialCategoryStore?: MaterialCategoryStore
}

@inject(
  'state',
  'projectDateStore',
  'deliveryVehicleTypesStore',
  'offloadingEquipmentsStore',
  'locationAttributesStore',
  'companiesStore',
  'materialsStore',
  'projectMembersStore',
  'materialCategoryStore',
)
@observer
export default class DeliveryCalendarCard extends React.Component<IProps> {
  @observable private localShouldUseMockValues: boolean = false
  @observable private localContainerWidth: number = 0
  @observable private localContainerHeight: number = 0

  private deliveryTitleContainer: HTMLDivElement
  private readonly store: DeliveryCalendarCardStore
  private readonly cardFieldsStore: DeliveryCardFieldsConfigStore

  public constructor(props: IProps) {
    super(props)

    this.localContainerHeight = props.containerHeight
    this.localContainerWidth = props.containerWidth
    this.localShouldUseMockValues = props.shouldUseMockValues

    this.store = new DeliveryCalendarCardStore()

    this.cardFieldsStore = new DeliveryCardFieldsConfigStore(
      props.delivery,
      props.state,
      props.projectDateStore,
      props.locationAttributesStore,
      props.deliveryVehicleTypesStore,
      props.offloadingEquipmentsStore,
      props.companiesStore,
      props.materialsStore,
      props.projectMembersStore,
      props.materialCategoryStore,
    )
    makeObservable(this)
  }

  @action
  public componentDidUpdate(prevProps: IProps) {
    const { delivery, containerHeight, containerWidth, shouldUseMockValues } =
      this.props
    this.localContainerHeight = containerHeight
    this.localContainerWidth = containerWidth
    this.localShouldUseMockValues = shouldUseMockValues

    if (delivery !== prevProps.delivery) {
      this.cardFieldsStore.init(delivery)
    }

    this.changeStatusRowAlignment()
  }

  public render() {
    const {
      delivery,
      backgroundColor: propsBackgroundColor,
      border,
      isRotated,
      className,
      isShared,
    } = this.props

    const { isDone, isCanceled } = delivery

    const backgroundColor = isShared ? Colors.neutral100 : propsBackgroundColor

    return (
      <div
        className={classList({
          'full-height full-width px5 brada4': true,
          [className]: !!className,
          opacity7: isDone && !isShared,
          opacity6: isCanceled && !isShared,
          'shared-delivery-card': isShared,
        })}
        style={{ border: !isShared ? border : undefined }}
      >
        <div
          className="event-card-background full-width full-height"
          style={{ backgroundColor }}
        />
        <div
          className={toggleClass('rotated-event-label relative pl4', isRotated)}
        >
          <div className="row" ref={this.setDeliveryTitleContainerRef}>
            <div
              className={classList({
                'relative row full-width': true,
                wrap: !isRotated,
              })}
            >
              {isShared ? this.sharedDeliveryTitle : this.deliveryTitle}
            </div>
          </div>
          <div
            className={classList({
              'no-grow text large grey-light': true,
              'white-space-normal': this.shouldCombineInfoIntoRow,
              col: !this.shouldCombineInfoIntoRow && !isRotated,
            })}
          >
            {!delivery.isShared && this.attributeValueElements}
          </div>
        </div>
      </div>
    )
  }

  private get sharedDeliveryTitle(): JSX.Element {
    const {
      delivery,
      sharedInfo: { companyName },
    } = this.props

    return (
      <span className="row y-start">
        <span className="text large bold shared-delivery-text">
          {this.renderDeliveryIcon(true)}
          {companyName}
          {companyName && (
            <span className="mr4">
              <DeliveryStatusText
                className="relative text large bold capitalize mr4"
                delivery={delivery}
              />
            </span>
          )}
        </span>
        <div className="delivery-shared-icon-holder no-grow">
          <Icons.SharedEntity className="delivery-shared-icon" />
        </div>
      </span>
    )
  }

  private get deliveryTitle(): JSX.Element[] {
    const { shouldUseMockValues, delivery, companiesStore } = this.props
    const { shouldAlignStatusToEnd } = this.store
    const { titleCardFields, getFieldValueById } = this.cardFieldsStore

    return titleCardFields.map((f, index) => {
      const fieldValue = getFieldValueById(f.fieldId, shouldUseMockValues)
      const displayValue = Array.isArray(fieldValue)
        ? fieldValue.join(', ')
        : fieldValue
      const truncatedFieldValue = truncateText(displayValue, MAX_NAME_LENGTH)
      const isStatusField = f.fieldId === FieldIds.STATUS
      const isCompanyField = f.fieldId === FieldIds.COMPANY

      const shouldUseNoGrowClass =
        !isStatusField || !index || (isStatusField && !shouldAlignStatusToEnd)

      return (
        <div
          key={f.fieldId}
          className={classList({
            row: true,
            'no-grow': shouldUseNoGrowClass,
          })}
        >
          {!index && this.renderDeliveryIcon()}
          {isStatusField ? (
            <DeliveryStatusText
              className="relative text large bold capitalize mr4"
              delivery={delivery}
              shouldAlignStatusToEnd={shouldAlignStatusToEnd}
              status={delivery.status}
            />
          ) : (
            <>
              <span
                title={displayValue}
                className="mr5 text large bold brand-dark"
              >
                {truncatedFieldValue}
              </span>
              {!delivery?.isShared && isCompanyField && (
                <span className="text large light mr4">
                  {delivery.codeToDisplay(companiesStore)}
                </span>
              )}
            </>
          )}
        </div>
      )
    })
  }

  private get attributeValueElements(): JSX.Element[] {
    const { titleCardFields } = this.cardFieldsStore
    const isTitleRowEmpty = !titleCardFields?.length

    return this.cardAttributeValues.map((value, index) => (
      <React.Fragment key={value.fieldId}>
        {this.renderAttributeField(value, !!index, isTitleRowEmpty && !index)}
      </React.Fragment>
    ))
  }

  private renderAttributeField = (
    field: ICardAttribute,
    shouldAddDot: boolean,
    shouldAddIcon: boolean,
  ): JSX.Element => {
    const iconContent = shouldAddIcon && this.renderDeliveryIcon()

    if (!this.shouldCombineInfoIntoRow && !this.props.isRotated) {
      return (
        <span
          title={field.fieldValue}
          className={classList({
            'text large grey-light': true,
            bold: field.isBold,
          })}
        >
          {iconContent}
          {field.fieldValue}
        </span>
      )
    }

    return (
      <>
        {iconContent}
        {shouldAddDot && ' • '}
        {field.isBold ? <b>{field.fieldValue}</b> : field.fieldValue}
      </>
    )
  }

  private renderDeliveryIcon(isShared?: boolean): JSX.Element {
    const {
      delivery: { isFromConcreteDirect },
      color,
    } = this.props

    return (
      <DeliveryIcon
        isFromCD={isFromConcreteDirect}
        className={classList({
          'no-grow mr4': true,
          'delivery-concrete-icon': isFromConcreteDirect,
          'delivery-icon': !isFromConcreteDirect,
          'shared-delivery-main-icon': isShared,
        })}
        color={isShared ? Colors.neutral50 : color}
      />
    )
  }

  @action.bound
  private changeStatusRowAlignment() {
    if (this.deliveryTitleContainer) {
      const { scrollHeight } = this.deliveryTitleContainer
      this.store.setStatusAlignment(scrollHeight < MIN_CARD_TITLE_ROW_HEIGHT)
    }
  }

  private setDeliveryTitleContainerRef = (ref: HTMLDivElement) => {
    this.deliveryTitleContainer = ref
  }

  @computed
  private get cardAttributeValues(): ICardAttribute[] {
    return this.cardFieldsStore.getCardAttributeValues(
      this.localShouldUseMockValues,
    )
  }

  @computed
  private get shouldCombineInfoIntoRow(): boolean {
    const scrollHeight = this.deliveryTitleContainer?.scrollHeight || 0
    const attributesHeight =
      this.cardAttributeValues.length * CARD_ROW_HEIGHT + scrollHeight

    return (
      attributesHeight > (this.localContainerHeight || 0) &&
      (this.localContainerWidth || 0) > MIN_CARD_WIDTH
    )
  }

  private setIgnoreTooltip = () => {
    this.props.setIgnoreTooltip?.(true)
  }

  private setToNotIgnoreTooltip = () => {
    this.props.setIgnoreTooltip?.(false)
  }
}
